import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from '@/components/ui/multiselect';
import { User, UserRole, DEFAULT_PERMISSIONS, ROLE_DESCRIPTIONS, ROLE_LABELS, UserPermissions } from '@/types/user';
import { mockCompanies } from '@/lib/mock-users';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onUpdateUser: (userId: string, data: Partial<User>) => void;
}

export function EditUserDialog({ open, onOpenChange, user, onUpdateUser }: EditUserDialogProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<User>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) {
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleRoleChange = (role: UserRole) => {
    setFormData({
      ...formData,
      role,
      permissions: DEFAULT_PERMISSIONS[role],
    });
  };

  const handlePermissionChange = (permission: keyof UserPermissions, checked: boolean) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions!,
        [permission]: checked,
      },
    });
  };

  const handleSubmit = () => {
    if (user) {
      onUpdateUser(user.id, formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({});
    setErrors({});
    onOpenChange(false);
  };

  const companyOptions = mockCompanies.map(company => ({
    value: company.id,
    label: company.name,
  }));

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          {user.updatedAt && (
            <p className="text-sm text-muted-foreground">
              Last modified: {format(new Date(user.updatedAt), 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step
                      ? 'bg-primary text-primary-foreground'
                      : s < step
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-0.5 ${
                      s < step ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Basic Information */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    First Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    value={formData.firstName || ''}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-destructive">{errors.firstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    Last Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={formData.lastName || ''}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-destructive">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@company.com"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          )}

          {/* Step 2: Role & Permissions */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>Select Role</Label>
                <RadioGroup value={formData.role} onValueChange={(value) => handleRoleChange(value as UserRole)}>
                  {(['administrator', 'user', 'view_only'] as UserRole[]).map((role) => (
                    <div key={role} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent">
                      <RadioGroupItem value={role} id={role} className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor={role} className="font-medium cursor-pointer">
                          {ROLE_LABELS[role]}
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          {ROLE_DESCRIPTIONS[role]}
                        </p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Permissions</Label>
                <div className="grid grid-cols-2 gap-3">
                  {formData.permissions && Object.entries(formData.permissions).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={key}
                        checked={value}
                        onCheckedChange={(checked) => 
                          handlePermissionChange(key as keyof UserPermissions, checked as boolean)
                        }
                      />
                      <Label htmlFor={key} className="text-sm font-normal cursor-pointer">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Company Access */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Assign Companies</Label>
                <p className="text-sm text-muted-foreground">
                  Select which companies this user can access
                </p>
                <MultiSelect
                  options={companyOptions}
                  value={formData.companies || []}
                  onValueChange={(companies) => setFormData({ ...formData, companies })}
                  placeholder="Select companies..."
                />
              </div>

              {formData.companies && formData.companies.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected Companies ({formData.companies.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.companies.map((companyId) => {
                      const company = mockCompanies.find((c) => c.id === companyId);
                      return (
                        <div
                          key={companyId}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                        >
                          {company?.name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {step < 3 ? (
                <Button onClick={handleNext}>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit}>Save Changes</Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
